// Pricing seam (frontend) — mirrors the backend PricingService contract.
//
// Pricing rule (Q19, hybrid): retail products → Shopify primary / Moloni
// fallback; PRO services → Moloni primary / Shopify fallback. The rule lives
// server-side behind GET /api/pricing; this module fetches it (5-min cache)
// and falls back to catalog-row values when the API is unreachable. ALL
// price/time rendering must go through this module. Contract: unknown is
// `null`, never 0 — the UI shows "sob consulta" instead of 0,00 €.

import { useEffect, useState } from 'react';
import type { SywComponent } from '../hooks/useSywData';

export interface SkuPricing {
  priceEur: number | null;
  installMinutes: number | null;
  isCustomOrder?: boolean; // set by backend when price was formula-calculated
}

// Current source: whatever the catalog row carries (Shopify variant price or
// SYW component). Upstream 0 means "not priced yet", not "free".
export function pricingOf(c: Pick<SywComponent, 'priceEur' | 'installMinutes'>): SkuPricing {
  return {
    priceEur: c.priceEur > 0 ? c.priceEur : null,
    installMinutes: c.installMinutes > 0 ? c.installMinutes : null,
  };
}

// ─── Remote pricing (GET /api/pricing → backend PricingService) ─────────────

const CACHE_TTL_MS = 5 * 60 * 1000;
const CHUNK = 50; // backend caps at 100 skus per request

const remoteCache = new Map<string, { data: SkuPricing; expiresAt: number }>();

export async function fetchPricing(
  skus: string[],
): Promise<Record<string, SkuPricing>> {
  const out: Record<string, SkuPricing> = {};
  const missing: string[] = [];

  for (const sku of new Set(skus)) {
    const hit = remoteCache.get(sku);
    if (hit && hit.expiresAt > Date.now()) out[sku] = hit.data;
    else missing.push(sku);
  }

  for (let i = 0; i < missing.length; i += CHUNK) {
    const chunk = missing.slice(i, i + CHUNK);
    try {
      const res = await fetch(`/api/pricing?skus=${encodeURIComponent(chunk.join(','))}`);
      if (!res.ok) continue;
      const json = (await res.json()) as {
        success: boolean;
        data: Array<{ sku: string; priceEur: number | null; installMinutes: number | null; isCustomOrder?: boolean }>;
      };
      if (!json.success) continue;
      for (const p of json.data) {
        const entry: SkuPricing = {
          priceEur: p.priceEur,
          installMinutes: p.installMinutes,
          isCustomOrder: p.isCustomOrder ?? false,
        };
        remoteCache.set(p.sku, { data: entry, expiresAt: Date.now() + CACHE_TTL_MS });
        out[p.sku] = entry;
      }
    } catch {
      // API unreachable — callers keep the pricingOf() catalog fallback.
    }
  }
  return out;
}

// Remote pricing for the given SKUs; empty record while loading/offline.
export function usePricing(skus: string[]): Record<string, SkuPricing> {
  const [pricing, setPricing] = useState<Record<string, SkuPricing>>({});
  const key = [...new Set(skus)].sort().join(',');

  useEffect(() => {
    if (!key) {
      setPricing({});
      return;
    }
    let cancelled = false;
    void fetchPricing(key.split(',')).then((p) => {
      if (!cancelled) setPricing(p);
    });
    return () => {
      cancelled = true;
    };
  }, [key]);

  return pricing;
}

// Catalog-row pricing merged with the remote (authoritative) value.
export function mergePricing(catalog: SkuPricing, remote?: SkuPricing): SkuPricing {
  return {
    priceEur: remote?.priceEur ?? catalog.priceEur,
    installMinutes: remote?.installMinutes ?? catalog.installMinutes,
  };
}

const eur = new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' });

export function formatPrice(priceEur: number | null): string {
  return priceEur !== null && priceEur > 0 ? eur.format(priceEur) : 'sob consulta';
}

// null when unknown — callers should omit the element entirely.
export function formatInstall(installMinutes: number | null): string | null {
  if (installMinutes === null || installMinutes <= 0) return null;
  if (installMinutes < 60) return `≈${installMinutes} min`;
  const h = Math.floor(installMinutes / 60);
  const m = installMinutes % 60;
  return m === 0 ? `≈${h}h` : `≈${h}h${String(m).padStart(2, '0')}`;
}
